import { Spin, ThemeConfig, MappingAlgorithm } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import {
  boxRadiusComponents,
  colors as uiColors,
  typography,
} from "@libertify/ui";

export type CustomToken = {
  cardBorder?: string;
  cardBoxShadow?: string;
  colorBgShowWrapper?: string;
  colorSecondary?: string;
  colorSecondaryBg?: string;
  colorSecondaryBorder?: string;
  colorOutlineBorder?: string;
  filterDropShadow?: string;
  colorScrollbarTrack?: string;
  colorTextTitle?: string;
  colorTextDesignSystem?: string;
  colorModalBgDesignSystem?: string;
  colorWhite?: string;
  colorBlack?: string;
  colorLightGrey?: string;
  colorGrey?: string;
  colorMidGrey?: string;
  colorTextBaseRedesign?: string;
  colorCardRedesign?: string;
  colorCardSecondaryRedesign?: string;
  colorStatusLive?: string;
  colorStatusReady?: string;
  colorStatusErrored?: string;
  colorStatusInProgress?: string;
  colorStatusActionNeeded?: string;
  colorBgDisabled?: string;
  colorTextBtnDisabled?: string;
  colorBorderDisabled?: string;
  colorSurfaceBG?: string;
  colorInfoBg?: string;
  colorInfoBorder?: string;
  colorInfo?: string;
};

export interface CustomThemeConfig extends ThemeConfig {
  token: ThemeConfig["token"] & CustomToken;
}
export function getCustomTheme(
  mode: string,
  defaultAlgorithm: MappingAlgorithm,
  darkAlgorithm: MappingAlgorithm,
  colors: {
    primary: string; // docustream logo color
    white: string;
    black: string;
  }
): CustomThemeConfig {
  const getPrimaryColor = () => {
    if (mode !== "light") {
      return colors.primary;
    } else {
      return "#3BC6BD";
    }
  };
  const getColorSecondaryBg = () => {
    if (mode === "light") {
      return "#dcdbee";
    } else {
      return "#1A0036";
    }
  };

  const getColorTextDescription = () => {
    if (mode === "light") {
      return "#BABABA";
    } else {
      return "#757575";
    }
  };

  const getColorTextTitle = () => {
    if (mode === "light") {
      return "#000000";
    } else {
      return "#ffffff";
    }
  };

  const getColorTextBaseRedesign = () => {
    if (mode === "light") {
      return "#3C3C3C";
    } else {
      return "#ffffff";
    }
  };

  const getColorCardRedesign = () => {
    if (mode === "light") {
      return "#FFFFFF";
    } else {
      return "#141414";
    }
  };

  const getColorCardSecondaryRedesign = () => {
    if (mode === "light") {
      return "#FFFFFF";
    } else {
      return "#f4e8ff";
    }
  };

  const getCardBoxShadow = () => {
    if (mode === "light") {
      return "";
    } else {
      return "0 0 10px #424242";
    }
  };

  const getCardBorder = () => {
    if (mode === "light") {
      return "1px solid #D2D2D2";
    } else {
      return "";
    }
  };

  const getColorBgShowWrapper = () => {
    if (mode === "light") {
      return "transparent";
    } else {
      return "#00000";
    }
  };

  let colorBgLayout = "#000000";
  if (mode === "light") {
    colorBgLayout = "#f5f5f5";
  }

  return {
    algorithm: mode === "light" ? defaultAlgorithm : darkAlgorithm,
    // ...(mode === "light" ? light : dark),

    token: {
      cardBorder: getCardBorder(),
      cardBoxShadow: getCardBoxShadow(),
      colorBgShowWrapper: getColorBgShowWrapper(),
      colorBgBlur: mode === "light" ? "#ffffff99" : "#000000cc",
      fontFamily: typography.fontFamily.secondary,
      // Brand ink for body text and headings (light mode).
      ...(mode === "light"
        ? {
            colorText: uiColors.secondary[700],
            colorTextHeading: uiColors.secondary[700],
          }
        : {}),
      colorSecondaryBorder: "#3700DE",
      colorOutlineBorder: mode === "light" ? "#000000" : "#222222", // used in border of the brandkit mockup for instance
      // fontSize: 20,
      //colorPrimary: mode !== "light" ? "#FF5A60" : "#FF5A60",
      colorPrimary: getPrimaryColor(),
      colorSecondary: "#3700DE",
      colorPrimaryBgHover: "#F0FFFB",
      colorSecondaryBg: getColorSecondaryBg(),
      colorTextDescription: getColorTextDescription(),
      colorTextTitle: getColorTextTitle(),
      // TODO: REVIEW TOKENS FOR NEW DESIGN SYSTEM, ADDING NEXT TWO SINCE THEY ARE DIFFERENT THAN CURRENT COLORS
      colorTextDesignSystem: "#0F1724",
      colorModalBgDesignSystem: "#FAFBFF",
      colorWhite: "#FFFFFF",
      colorGrey: "#9D9D9D",
      colorBlack: "#3C3C3C",
      colorLightGrey: "#F3F4F6",
      colorMidGrey: "#edeff3",
      colorTextBase: getColorTextBaseRedesign(),
      colorCardRedesign: getColorCardRedesign(),
      colorCardSecondaryRedesign: getColorCardSecondaryRedesign(),
      colorBgLayout,
      filterDropShadow: "drop-shadow(0px 3px 10px rgba(0, 0, 0, 0.09))",
      colorTextDisabled: "#D2D2D2",
      colorBgDisabled: "#F0F0F0",
      colorTextBtnDisabled: "#A0A0A0",
      colorBorderDisabled: "#D9D9D9",
      colorStatusLive: "#dcfce7",
      colorStatusReady: "#ccfbf1",
      colorStatusErrored: "#FF5A60",
      colorStatusInProgress: "#DBEAFE",
      colorStatusActionNeeded: "#F5C542",
      colorSurfaceBG: "#FAFBFF",
      colorInfoBg: "#DFEFFB",
      colorInfoBorder: "#61B1EC",
      colorInfo: "#61B1EC",
    },
    components: {
      // Boxes (Card, Modal, Popover, Dropdown) use the shared box radius.
      ...boxRadiusComponents,
      Tree: {
        // directoryNodeSelectedColor: colors.white,
        // nodeSelectedBg: colors.primary,
        // directoryNodeSelectedColor: "red",
      },
      Badge: {
        statusSize: 8,
      },
      Button: {
        colorText: getColorTextDescription(),
        colorBorder: getColorTextDescription(),
      },
      Table: {
        headerColor: getColorTextDescription(),
        headerBg: colorBgLayout,
        headerSortActiveBg: colorBgLayout,
        colorBgContainer: colorBgLayout,
      },
      Alert: {
        colorInfoBg: "#DFEFFB",
        colorInfoBorder: "#61B1EC",
        colorInfo: "#61B1EC",

        colorWarningBg: "#FDEFD8",
        colorWarningBorder: "#F6AF3D",
        colorWarning: "#F6AF3D",

        colorErrorBg: "#FFDDDC",
        colorErrorBorder: "#FF554F",
        colorError: "#FF554F",

        colorSuccessBg: "#E6F3E6",
        colorSuccessBorder: "#83C182",
        colorSuccess: "#83C182",
      },
    },
  };
}

Spin.setDefaultIndicator(<LoadingOutlined spin />);
